<template>
  <Grid
    class="products-grid"
    :pageable="controller.pageable.value"
    :data-items="products"
    :skip="controller.skip.value"
    :take="controller.take.value"
    :total="totalProducts"
    :columns="controller.columns.value"
    :loader="productsLoading"
    :filter="filter"
    :column-menu="!productsLoading"
    :sort="sort"
    :resizable="true"
    :reorderable="true"
    @columnresize="controller.onColumnResize($event)"
    @columnreorder="onColumnReorder"
    @filterchange="controller.filterChangeHandler($event)"
    @sortchange="controller.sortChangeHandler($event)"
    @pagechange="controller.pageChangeHandler($event)"
    data-cy="products-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="controller.columns.value"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:DetailsCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <img
          class="fa fa-external-link"
          alt="Detail Icon"
          :src="detailIconUrl"
          @click.prevent="openProductDetail(dataItem)"
        />
      </td>
    </template>
    <template v-slot:UkdIdCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span v-if="!dataItem.DoNotDisplayUkdId">{{ dataItem[field] }}</span>
      </td>
    </template>
    <template v-slot:CompanyCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <a
          :title="dataItem[field]"
          :href="`mailto:${(dataItem as OuKashUnionUkdProducts).CompanyRcEmail}`"
          >{{ dataItem[field] }}</a
        >
      </td>
    </template>
    <template v-slot:ProductLocCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <a
          v-if="dataItem[field]"
          class="is-download-link"
          @click="
            downloadSingleLoc((dataItem as OuKashUnionUkdProducts).VueFullUkdId)
          "
          ><font-awesome-icon icon="fa-solid fa-arrow-down" /> LOC</a
        >
      </td>
    </template>
    <template v-slot:CompanyLocCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <a
          v-if="dataItem[field]"
          class="is-download-link"
          @click="
            downloadLoc((dataItem as OuKashUnionUkdProducts).VueFullUkdId)
          "
          ><font-awesome-icon icon="fa-solid fa-arrow-down" /> LOC</a
        >
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import detailIconUrl from "@/assets/img/DetailIcon.png";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";

import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { getFilterOperatorsForBoolean } from "@/shared/helpers/filtersHelper";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import GridController from "@/shared/services/GridController";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import {
  printLocAsync,
  printLocUkdSingleProductAsync,
} from "@/shared/helpers/printLocHelper";
import { useMainStore } from "@/stores/useMainStore";
import type { LocPrintRequest } from "@/Domain/LocPrintRequest";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  products: OuKashUnionUkdProducts[];
  productsLoading: boolean;
  pageSize: number;
  skip: number;
  totalProducts: number;
  sort: SortDescriptor[];
  filter: CompositeFilterDescriptor;
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<OuKashUnionUkdProducts[]>;
}>();

const emit = defineEmits<{
  (e: "productDetailsClicked", product: OuKashUnionUkdProducts): void;
  (e: "itemsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const columns = [
  {
    title: " ",
    cell: "DetailsCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  },
  {
    title: "Label Name",
    field: "LabelName",
    columnMenu: "columnMenu",
    width: "150px",
  },
  {
    title: "Label Number",
    field: "LabelNum",
    columnMenu: "columnMenu",
    width: "120px",
  },
  {
    title: "Company",
    field: "CompanyName",
    columnMenu: "columnMenu",
    width: "100px",
  },
  {
    title: "Brand Name",
    field: "BrandName",
    columnMenu: "columnMenu",
    width: "100px",
  },
  {
    title: "DPM Status",
    field: "Dpm",
    columnMenu: "columnMenu",
    onlyValueFilter: true,
    width: "100px",
  },
  {
    title: "Comment",
    field: "Comments",
    columnMenu: "columnMenu",
  },
  {
    title: "C/I",
    field: "CI",
    columnMenu: "columnMenu",
  },
  {
    title: "Passover",
    field: "Pesach",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForBoolean(),
  },
  {
    title: "UKD ID",
    field: "VueFullUkdId",
    cell: "UkdIdCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Agency",
    field: "AgencyName",
    columnMenu: "columnMenu",
  },
  {
    title: "Company Rabbi",
    field: "CompanyRc",
    cell: "CompanyCell",
    columnMenu: "columnMenu",
    width: "150px",
  },
  {
    title: "Country",
    field: "CompanyCountry",
    columnMenu: "columnMenu",
  },
  {
    title: "Product LOC",
    field: "IsOkToPrintLoc",
    cell: "ProductLocCell",
    sortable: false,
    filterable: false,
  },
  {
    title: "Company LOC",
    field: "isOkToPrintCompanyLoc",
    cell: "CompanyLocCell",
    sortable: false,
    filterable: false,
  },
] as GridColumnPropsExtended[];

const gridName = "ProductsGrid";

const controller = new GridController<OuKashUnionUkdProducts>(
  gridName,
  columns,
  emit,
  props.pageSize
);

controller.initOptions();

const openProductDetail = function (product: OuKashUnionUkdProducts) {
  emit("productDetailsClicked", product);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  controller.onColumnsSubmit(columnsProps);
  emit("columnssubmit", columnsProps);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  controller.columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const resetColumnSelection = function () {
  controller.columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  controller.columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

const downloadLoc = (id: string) => {
  printLocAsync({
    CompanyLogin: main.companyLogin,
    LocReportIsFullLoc: true,
    UkdId: id,
    LocCalledFrom: "~/UkdProductList.aspx",
  } as LocPrintRequest);
};

const downloadSingleLoc = (id: string) => {
  printLocUkdSingleProductAsync({
    UkdId: id,
  });
};

defineExpose({
  executeAction,
});

watchEffect(() => {
  controller.skip.value = props.skip;
});

watchEffect(() => {
  controller.take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.products-grid {
  min-height: 450px;
  height: 800px;

  .fa-external-link {
    cursor: pointer;
  }
}
.k-grid {
  a.is-download-link {
    cursor: pointer;
    svg {
      height: 12px;
    }
    &:before {
      content: "";
    }
  }
}
</style>
